import { Injectable } from '@angular/core';
import { Observable, pipe, from, of, BehaviorSubject, forkJoin } from 'rxjs';
import { switchMap, tap, map, catchError, concatMap, filter, take, finalize } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { sortBy, extend, each } from 'lodash';

import { Envelope } from '@app/utils/flume-api.interceptor';
import { Model } from '@app/models/model';
import { Resolver } from '@app/models/fetch';
import { Page } from '@app/models/page';

export interface CacheInfo {
    n: string;
    t: number;
    d: string;
    // Contains the sections that are cached
    s: Array<{
        // The datetime it was cached
        d: string;
        // The page that is cached
        p?: string;
        // The query that is cached
        q?: string;
    }>;
}

// eslint-disable-next-line no-shadow
export enum CacheEvent {
    IDLE,
    UPDATE,
}

export interface CacheLocalInfo {
    model: typeof Model;
    expire: number;
    loading: number;
    changes: BehaviorSubject<CacheEvent>;
}

export type CacheResults = [any[], CacheInfo];

export interface DBQuery {
    field: string;
    limit?: number;
    equals?: string | number;
    above?: string | number;
    aboveOrEqual?: string | number;
    below?: string | number;
    belowOrEqual?: string | number;
}

@Injectable({
    providedIn: 'root',
})
export class Cache {
    tables: Record<string, Record<string, { [x: string]: any; id: string | number }>> = {};
    cacheTable: Record<string, CacheInfo> = {};

    caches: Record<string, CacheLocalInfo> = {};

    constructor() {
        (window as any).cache = this;
    }

    register(name: string, model: any, expire?: number) {
        this.tables[name] = {};
        this.cacheTable[name] = {
            n: name,
            t: 0,
            d: null,
            s: [],
        };
        this.caches[name] = {
            model,
            expire: expire || 600,
            loading: 0,
            changes: new BehaviorSubject<CacheEvent>(CacheEvent.IDLE),
        };
    }

    getTable(cache: string) {
        return this.tables[cache];
    }

    /**
     * Searches for and returns a model in the cache. If it does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param id The id of the model to get
     * @param resolver An observable that resolves to the model if it is not found
     */
    getOne<T extends Model>(cache: string, id: string | number, resolver: Resolver<T>): Observable<T> {
        const tbl = this.getTable(cache);

        return of(tbl[id]).pipe(
            switchMap((results: any) => {
                if (!results) {
                    const cacheInfo = this.caches[cache];
                    cacheInfo.changes.next(CacheEvent.UPDATE);
                    return resolver.req.pipe(
                        this.insertPipe(cache, resolver, null, cacheInfo),
                        finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)),
                        map(() => tbl[id])
                    );
                }
                return of(results);
            })
        );
    }

    /**
     * Searches for and returns multiple models in the cache. If one does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param ids The ids of the models
     * @param resolver An observable that resolves to the model if it is not found
     */
    getSome<T extends Model>(cache: string, ids: string[] | number[], resolver: Resolver<T>): Observable<T[]> {
        let models = [];
        let noResult = false;
        const cacheQueryFn = () => {
            models = [];
            for (let i = 0; i < ids.length; i++) {
                const val = this.getTable(cache)[ids[i]];
                models.push(val);
                if (!val) {
                    noResult = true;
                }
            }
        };

        return this.waitUntilCacheIdle(this.caches[cache]).pipe(
            switchMap(() => {
                cacheQueryFn();

                if (noResult) {
                    const cacheInfo = this.caches[cache];
                    cacheInfo.changes.next(CacheEvent.UPDATE);
                    return resolver.req.pipe(
                        this.insertPipe(cache, resolver, null, cacheInfo),
                        finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)),
                        map(() => {
                            cacheQueryFn();
                            return models;
                        })
                    );
                }
                return of(models);
            })
        );
    }

    /**
     * Searches for and joins multiple models in the cache. If one does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param ids The ids of the models
     * @param resolver An observable that resolves to the model if it is not found
     */
    joinSome(
        cache: string,
        models: any[],
        idField: string,
        joinField: string,
        resolver: Resolver<any>
    ): Observable<any[]> {
        let noResult = false;
        const cacheQueryFn = () => {
            for (let i = 0; i < models.length; i++) {
                let val;
                if (!models[i][joinField]) {
                    val = this.getTable(cache)[models[i][idField]];
                    models[i][joinField] = val;
                }
                if (!val) {
                    noResult = true;
                }
            }
        };

        return this.waitUntilCacheIdle(this.caches[cache]).pipe(
            switchMap(() => {
                cacheQueryFn();

                if (noResult) {
                    const cacheInfo = this.caches[cache];
                    cacheInfo.changes.next(CacheEvent.UPDATE);
                    return resolver.req.pipe(
                        this.insertPipe(cache, resolver, null, cacheInfo),
                        finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)),
                        map(() => {
                            cacheQueryFn();
                            return models;
                        })
                    );
                }
                return of(models);
            })
        );
    }

    getPage<T extends Model>(cache: string, page: Page, resolver: Resolver<T>): Observable<T[]> {
        const cacheInfoQuery = this.cacheTable[cache];

        return this.waitUntilCacheIdle(this.caches[cache]).pipe(
            map(() => cacheInfoQuery),
            this.checkForPage(page, this.caches[cache]),
            switchMap((data) => {
                if (!data.page) {
                    const cacheInfo = this.caches[cache];
                    cacheInfo.changes.next(CacheEvent.UPDATE);
                    return resolver.req.pipe(
                        this.insertPipe(cache, resolver, data.info, this.caches[cache]),
                        finalize(() => cacheInfo.changes.next(CacheEvent.IDLE)),
                        map((d) => d.page)
                    );
                }
                return of(data.page);
            }),
            map((cachePage: Page) => this.getPageQuery(cache, page, cachePage) as T[])
        );
    }

    /**
     * Returns all of the models in the cache. If the cache is expired,
     * subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param resolver An observable that resolves to the model if it is not found
     * @param sortField The field to sort the data on
     * @param sortDir The sort direction
     */
    getAll<T extends Model>(
        cache: string,
        resolver: Resolver<T>,
        sortField?: string,
        sortDir?: 'ASC' | 'DESC'
    ): Observable<T[]> {
        const cacheInfoQuery = this.cacheTable[cache];

        return this.waitUntilCacheIdle(this.caches[cache]).pipe(
            map(() => cacheInfoQuery),
            switchMap((results: CacheInfo) => {
                if (this.cacheExpired(results)) {
                    const cacheInfo = this.caches[cache];
                    cacheInfo.changes.next(CacheEvent.UPDATE);
                    return resolver.req.pipe(
                        this.insertPipe(cache, resolver, results, cacheInfo),
                        finalize(() => cacheInfo.changes.next(CacheEvent.IDLE))
                    );
                }
                return of(null);
            }),
            map(() => {
                let models = sortBy(this.getTable(cache), sortField || 'id');
                if (sortDir === 'DESC') {
                    models = models.reverse();
                }
                return models as T[];
            })
        );
    }

    put(cache: string, models: any[]) {
        const cacheInfo = this.caches[cache];
        cacheInfo.changes.next(CacheEvent.UPDATE);
        const tbl = this.getTable(cache);
        for (const model of models) {
            tbl[model.id] = model;
        }
        cacheInfo.changes.next(CacheEvent.IDLE);
        return of(null);
    }

    private mapToClassPipe(cache: string) {
        return pipe(
            map((models: any) => {
                if (Array.isArray(models)) {
                    for (const obj of models) {
                        const model = Object.create(this.caches[cache].model);
                        for (const m of obj) {
                            if (obj.hasOwnProperty(m)) {
                                try {
                                    model[m] = obj[m];
                                } catch (_) {}
                            }
                        }
                    }
                } else if (typeof models === 'object') {
                    const model = Object.create(this.caches[cache].model);
                    for (const m of models) {
                        if (models.hasOwnProperty(m)) {
                            try {
                                model[m] = models[m];
                            } catch (_) {}
                        }
                    }
                }
            })
        );
    }

    private insertPipe(cache: string, resolver: Resolver<any>, cacheInfo: CacheInfo, cacheLocalInfo: CacheLocalInfo) {
        return pipe(
            tap((data: Envelope<any>) => {
                const models: Model[] = [];

                cacheLocalInfo.changes.next(CacheEvent.UPDATE);
                // Create and populate new models from API data
                for (const obj of data.data) {
                    models.push(new cacheLocalInfo.model(obj));
                }

                // If the request is for all models, clear the table
                // and add new models. Otherwise, put all models into
                // table
                if (resolver.params && resolver.params.all) {
                    this.tables[cache] = {};
                }
                for (const model of models) {
                    this.tables[cache][model.id] = model;
                }

                if (cacheInfo) {
                    const cacheUpdate: Partial<CacheInfo> = {};
                    if (resolver.params && resolver.params.all) {
                        cacheUpdate.d = DateTime.local().toISO();
                        cacheUpdate.t = data.count;
                    } else if (resolver.page) {
                        resolver.page.cursor = models[0][resolver.page.sortField];
                        cacheUpdate.s = this.filterSections(cache, cacheInfo).s;
                        cacheUpdate.s.push({
                            d: DateTime.utc().toISO(),
                            p: resolver.page.serialize(),
                        });
                    }

                    this.cacheTable[cacheInfo.n] = extend(this.cacheTable[cacheInfo.n], cacheUpdate);
                }
            }),
            map(() => resolver),
            tap(() => cacheLocalInfo.changes.next(CacheEvent.IDLE)),
            catchError((val) => {
                cacheLocalInfo.changes.next(CacheEvent.IDLE);
                throw val;
            })
        );
    }

    private waitUntilCacheIdle(cache: CacheLocalInfo) {
        return cache.changes.pipe(
            filter((change) => change === CacheEvent.IDLE),
            take(1)
        );
    }

    private waitUntilCacheIdlePipe(cache: CacheLocalInfo) {
        return pipe(switchMap((data) => this.waitUntilCacheIdle(cache).pipe(map(() => data))));
    }

    private cacheExpired(data: CacheInfo) {
        const cache = this.caches[data.n];

        if (
            !data.d ||
            (data.d &&
                cache.expire !== Infinity &&
                DateTime.fromISO(data.d).plus({ seconds: cache.expire }) < DateTime.local())
        ) {
            return true;
        }

        return false;
    }

    private checkForPage(page: Page, localCache: CacheLocalInfo) {
        return pipe(
            map((cacheInfo: CacheInfo) => {
                // Check if entire collection is fetched
                if (
                    cacheInfo.d &&
                    DateTime.fromISO(cacheInfo.d) > DateTime.local().minus({ seconds: localCache.expire })
                ) {
                    return {
                        page: new Page(1, Infinity, page.sortField, page.sortDirection),
                        info: cacheInfo,
                    };
                }

                // Find a valid page (either matching or a page that encompasses the input page)
                const match = cacheInfo.s.find((section) => section.p && page.compare(Page.deserialize(section.p)));
                return {
                    page: match && match.p && Page.deserialize(match.p),
                    info: cacheInfo,
                };
            })
        );
    }

    private getPageQuery(cache: string, page: Page, cachePage: Page) {
        const offset = (page.page - cachePage.page) * page.pageSize || 0;

        if (cachePage.cursor) {
            if (page.sortDirection === 'ASC') {
                const filtered = Object.values(this.getTable(cache)).filter(
                    (model) => model[page.sortField] >= cachePage.cursor
                );
                return sortBy(filtered, page.sortField).slice(offset, offset + page.limit);
            } else {
                const filtered = Object.values(this.getTable(cache)).filter(
                    (model) => model[page.sortField] <= cachePage.cursor
                );
                return sortBy(filtered, page.sortField)
                    .reverse()
                    .slice(offset, offset + page.limit);
            }
        } else {
            if (page.sortDirection === 'ASC') {
                return Object.values(this.getTable(cache)).slice(offset, offset + page.limit);
            } else {
                return Object.values(this.getTable(cache))
                    .reverse()
                    .slice(offset, offset + page.limit);
            }
        }
    }

    private filterSections(cache: string, cacheInfo: CacheInfo) {
        // eslint-disable-next-line max-len
        cacheInfo.s = cacheInfo.s.filter(
            (section) => DateTime.fromISO(section.d) > DateTime.local().minus({ second: this.caches[cache].expire })
        );

        return cacheInfo;
    }
}
